import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LayoutDashboard, LogOut, ShieldCheck } from 'lucide-react';
import FooterNew from '../components/FooterNew';
import { clearUserSession, getBackendUrl, getUserToken, setUserSession } from '../utils/auth';

const Login = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialMode = searchParams.get('mode') === 'register' ? 'register' : 'login';
  const [mode, setMode] = useState(initialMode);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [otpSent, setOtpSent] = useState(false);

  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
    password: '',
    otp: '',
  });
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
    otp: '',
  });

  const isLoggedIn = Boolean(getUserToken());
  const backendUrl = useMemo(() => getBackendUrl(), []);
  const redirectPath = searchParams.get('redirect') || '/dashboard';

  const resetAlerts = () => {
    setError('');
    setMessage('');
  };

  const formatAuthError = (requestError) => {
    const rawMessage = requestError?.message || 'Request failed';

    if (/failed to fetch|networkerror|load resource/i.test(rawMessage)) {
      return `Unable to connect to auth server (${backendUrl}). Please check backend server and CORS/domain settings.`;
    }

    return rawMessage;
  };

  const handleModeSwitch = (nextMode) => {
    setMode(nextMode);
    setOtpSent(false);
    resetAlerts();
  };

  const postJson = async (url, body) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    const payload = await response.json();

    if (!response.ok || !payload.success) {
      throw new Error(payload.message || 'Request failed');
    }

    return payload;
  };

  const sendRegisterOtp = async (event) => {
    event.preventDefault();
    resetAlerts();
    setLoading(true);

    try {
      const payload = await postJson(`${backendUrl}/api/auth/register-initiate`, {
        name: registerData.name,
        email: registerData.email,
        password: registerData.password,
      });

      setOtpSent(true);
      const otpHint = payload.debugOtp ? ` (Test OTP: ${payload.debugOtp})` : '';
      setMessage((payload.message || 'OTP sent. Check your email.') + otpHint);
    } catch (requestError) {
      setError(formatAuthError(requestError));
    } finally {
      setLoading(false);
    }
  };

  const verifyRegisterOtp = async (event) => {
    event.preventDefault();
    resetAlerts();
    setLoading(true);

    try {
      const payload = await postJson(`${backendUrl}/api/auth/register-verify`, {
        email: registerData.email,
        otp: registerData.otp,
      });

      setUserSession(payload.token, payload.user);
      navigate(redirectPath, { replace: true });
    } catch (requestError) {
      setError(formatAuthError(requestError));
    } finally {
      setLoading(false);
    }
  };

  const sendLoginOtp = async (event) => {
    event.preventDefault();
    resetAlerts();
    setLoading(true);

    try {
      const payload = await postJson(`${backendUrl}/api/auth/login-initiate`, {
        email: loginData.email,
        password: loginData.password,
      });

      setOtpSent(true);
      const otpHint = payload.debugOtp ? ` (Test OTP: ${payload.debugOtp})` : '';
      setMessage((payload.message || 'OTP sent. Check your email.') + otpHint);
    } catch (requestError) {
      setError(formatAuthError(requestError));
    } finally {
      setLoading(false);
    }
  };

  const verifyLoginOtp = async (event) => {
    event.preventDefault();
    resetAlerts();
    setLoading(true);

    try {
      const payload = await postJson(`${backendUrl}/api/auth/login-verify`, {
        email: loginData.email,
        otp: loginData.otp,
      });

      setUserSession(payload.token, payload.user);
      navigate(redirectPath, { replace: true });
    } catch (requestError) {
      setError(formatAuthError(requestError));
    } finally {
      setLoading(false);
    }
  };

  const handleUserLogout = () => {
    clearUserSession();
    setMode('login');
    setOtpSent(false);
    setRegisterData({ name: '', email: '', password: '', otp: '' });
    setLoginData({ email: '', password: '', otp: '' });
    setMessage('Logged out successfully');
    setError('');
  };

  return (
    <main className="bg-primary min-h-screen pt-28">
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="rounded-2xl p-7 sm:p-10"
          style={{
            background: 'linear-gradient(145deg, rgba(15, 23, 42, 0.9), rgba(30, 41, 59, 0.75))',
            border: '1px solid rgba(99, 102, 241, 0.2)',
          }}
        >
          <div className="text-center mb-8">
            <span className="inline-block px-4 py-1 rounded-full bg-accent/10 border border-accent/20 text-ai-glow text-sm font-medium">
              OTP Authentication
            </span>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mt-4">Login / Register</h1>
            <p className="text-slate-400 mt-2">AI features use karne ke liye secure OTP verification complete karein.</p>
          </div>

          {isLoggedIn ? (
            <div className="space-y-4">
              <p className="text-emerald-300 text-sm mb-2 text-center">You are already logged in.</p>
              <Link to="/dashboard" className="neon-btn neon-btn-outline w-full justify-center no-underline">
                <LayoutDashboard className="w-4 h-4" />
                Go To Dashboard
              </Link>
              <button
                type="button"
                onClick={handleUserLogout}
                className="neon-btn neon-btn-primary w-full justify-center"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          ) : (
            <>
              <div className="mb-7 text-center text-sm text-slate-400">
                {mode === 'login' ? (
                  <>
                    New user?{' '}
                    <button
                      type="button"
                      onClick={() => handleModeSwitch('register')}
                      className="text-ai-glow hover:text-white bg-transparent border-none p-0 cursor-pointer"
                    >
                      Create account
                    </button>
                  </>
                ) : (
                  <>
                    Already registered?{' '}
                    <button
                      type="button"
                      onClick={() => handleModeSwitch('login')}
                      className="text-ai-glow hover:text-white bg-transparent border-none p-0 cursor-pointer"
                    >
                      Login instead
                    </button>
                  </>
                )}
              </div>

              {error ? <p className="text-rose-300 text-sm mb-4">{error}</p> : null}
              {message ? <p className="text-emerald-300 text-sm mb-4">{message}</p> : null}

              {mode === 'register' ? (
                <form onSubmit={otpSent ? verifyRegisterOtp : sendRegisterOtp} className="space-y-4">
                  {!otpSent ? (
                    <>
                      <input
                        type="text"
                        required
                        value={registerData.name}
                        onChange={(event) => setRegisterData((prev) => ({ ...prev, name: event.target.value }))}
                        placeholder="Full Name"
                        className="w-full px-4 py-3 rounded-xl bg-surface/60 border border-accent/20 text-white"
                      />
                      <input
                        type="email"
                        required
                        value={registerData.email}
                        onChange={(event) => setRegisterData((prev) => ({ ...prev, email: event.target.value }))}
                        placeholder="Email"
                        className="w-full px-4 py-3 rounded-xl bg-surface/60 border border-accent/20 text-white"
                      />
                      <input
                        type="password"
                        required
                        minLength={6}
                        value={registerData.password}
                        onChange={(event) => setRegisterData((prev) => ({ ...prev, password: event.target.value }))}
                        placeholder="Password (min 6 chars)"
                        className="w-full px-4 py-3 rounded-xl bg-surface/60 border border-accent/20 text-white"
                      />
                    </>
                  ) : (
                    <input
                      type="text"
                      required
                      maxLength={6}
                      value={registerData.otp}
                      onChange={(event) => setRegisterData((prev) => ({ ...prev, otp: event.target.value }))}
                      placeholder="Enter 6-digit OTP"
                      className="w-full px-4 py-3 rounded-xl bg-surface/60 border border-accent/20 text-white tracking-[0.4em]"
                    />
                  )}

                  <button type="submit" disabled={loading} className="neon-btn neon-btn-primary w-full justify-center">
                    <ShieldCheck className="w-4 h-4" />
                    {loading ? 'Please wait...' : otpSent ? 'Verify & Complete Register' : 'Send OTP'}
                  </button>
                </form>
              ) : (
                <form onSubmit={otpSent ? verifyLoginOtp : sendLoginOtp} className="space-y-4">
                  {!otpSent ? (
                    <>
                      <input
                        type="email"
                        required
                        value={loginData.email}
                        onChange={(event) => setLoginData((prev) => ({ ...prev, email: event.target.value }))}
                        placeholder="Email"
                        className="w-full px-4 py-3 rounded-xl bg-surface/60 border border-accent/20 text-white"
                      />
                      <input
                        type="password"
                        required
                        value={loginData.password}
                        onChange={(event) => setLoginData((prev) => ({ ...prev, password: event.target.value }))}
                        placeholder="Password"
                        className="w-full px-4 py-3 rounded-xl bg-surface/60 border border-accent/20 text-white"
                      />
                    </>
                  ) : (
                    <input
                      type="text"
                      required
                      maxLength={6}
                      value={loginData.otp}
                      onChange={(event) => setLoginData((prev) => ({ ...prev, otp: event.target.value }))}
                      placeholder="Enter 6-digit OTP"
                      className="w-full px-4 py-3 rounded-xl bg-surface/60 border border-accent/20 text-white tracking-[0.4em]"
                    />
                  )}

                  <button type="submit" disabled={loading} className="neon-btn neon-btn-primary w-full justify-center">
                    <ShieldCheck className="w-4 h-4" />
                    {loading ? 'Please wait...' : otpSent ? 'Verify & Login' : 'Send OTP'}
                  </button>
                </form>
              )}
            </>
          )}

          <div className="mt-6 text-sm text-slate-400 text-center">
            Admin login chahiye?{' '}
            <Link to="/admin/login" className="text-ai-glow hover:text-white">
              Open Admin Panel Login
            </Link>
          </div>
        </motion.div>
      </section>
      <FooterNew />
    </main>
  );
};

export default Login;
